// The staff console's gate. One credential, one field pair, nothing else.
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import type { LoginResponse, SupportContact, WhereResponse } from '../../shared/api-staff.ts';
import { Button } from '../components/ui/button.tsx';
import { Field, FieldGroup, FieldLabel } from '../components/ui/field.tsx';
import { Input } from '../components/ui/input.tsx';
import { mount } from '../console/mount.tsx';
import * as console_ from '../lib/staff-console.ts';

// Same-host relative paths only; the server validates this again on the way
// back. An open redirect on a login page is free to prevent.
function next(): string {
  const raw = new URLSearchParams(location.search).get('next') ?? '/';
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
}

function Login(): ReactNode {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isBusy, setBusy] = useState(false);
  const [bad, setBad] = useState('');
  const [supportEmail, setSupportEmail] = useState<string | null>(null);
  // On a venue's own address (umami.<domain>) the page says whose console it
  // is; on the shared staff host it stays "Staff console".
  const [venueName, setVenueName] = useState<string | null>(null);
  const passwordReference = useRef<HTMLInputElement>(null);

  // The support contact is a company-wide constant supplied by the server, not
  // a per-venue column. It is the only thing on this page that comes from an
  // endpoint, and the page is perfectly usable without it.
  useEffect(() => {
    const live = { current: true };
    void (async (): Promise<void> => {
      let reply;
      try {
        reply = await console_.get<SupportContact>('/api/console/support');
      } catch {
        return; // The printed card on the wall is the fallback.
      }
      if (!live.current || !reply.ok || !reply.data.email) return;
      setSupportEmail(reply.data.email);
    })();
    void (async (): Promise<void> => {
      try {
        const reply = await console_.get<WhereResponse>('/api/console/where');
        if (live.current && reply.ok) setVenueName(reply.data.venueName);
      } catch {
        // The heading without the name is still a working sign-in page.
      }
    })();
    return () => {
      live.current = false;
    };
  }, []);

  function submit(event: SyntheticEvent): void {
    event.preventDefault();
    if (isBusy) return;
    setBad('');
    setBusy(true);

    void (async (): Promise<void> => {
      let reply;
      try {
        reply = await console_.post<LoginResponse & { retryAfter?: number }>('/api/console/login', {
          email: email.trim(),
          password,
        });
      } catch {
        setBusy(false);
        setBad("Can't reach the server. Check the wifi and try again.");
        return;
      }
      setBusy(false);
      if (reply.ok) {
        location.replace(next());
        return;
      }
      if (reply.status === 429) {
        // Never a lockout — only "too many at once". Say how long, in seconds.
        setBad(
          'Too many sign-ins at once. Try again in ' + String(reply.data.retryAfter ?? 1) + 's.',
        );
        return;
      }
      // One string for every failure mode: wrong email, wrong password, unknown
      // venue. Never reveal which.
      setBad('Wrong email or password.');
      setPassword('');
      passwordReference.current?.focus();
    })();
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-6">
      <form
        onSubmit={submit}
        noValidate
        className="w-full max-w-[380px] rounded-2xl border bg-card p-6 shadow-card"
      >
        <img src="/assets/brand/mark.png" alt="" className="mx-auto mb-2.5 block size-14" />
        <h1 className="m-0 text-center text-[21px] font-semibold">Paper Fish</h1>
        <p className="mt-1 mb-6 text-center text-sm text-muted-foreground">
          {venueName ? venueName + ' · Staff console' : 'Staff console'}
        </p>

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            {/* autocomplete matters more than usual here: one shared credential
                that nobody on the floor has memorised. Let the phone's password
                manager fill it. text-base is 16px, and anything smaller makes
                iOS Safari zoom the page on focus. */}
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              className="min-h-12 text-base"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              ref={passwordReference}
              className="min-h-12 text-base"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </Field>
        </FieldGroup>

        <Button type="submit" size="touch" className="mt-5 w-full" disabled={isBusy}>
          {isBusy ? 'Signing in…' : 'Sign in'}
        </Button>

        {/* A real link, 44 px, for the manager who is locked out: the reset
            flow is theirs to start, not the rep's. */}
        <Button variant="ghost" size="touch" className="mt-2 w-full" render={<a href="/forgot" />}>
          Forgot password?
        </Button>

        {bad ? (
          <p role="alert" className="mt-3.5 mb-0 text-sm text-destructive">
            {bad}
          </p>
        ) : null}

        <p className="mt-6 mb-0 text-[13px] leading-snug text-muted-foreground">
          Lost the password? Your Paper Fish rep can reset it
          {supportEmail ? (
            <>
              : <a href={'mailto:' + supportEmail}>{supportEmail}</a>
            </>
          ) : (
            '.'
          )}
        </p>
      </form>
    </main>
  );
}

mount(<Login />);
